import { pushDebugLog } from './store'

/**
 * Wraps the native fetch API to automatically log all requests and responses
 * to the debug console.
 *
 * @param source The source name to display in the console (e.g., 'api', 'github')
 * @param originalFetch The fetch implementation to wrap (defaults to window.fetch)
 */
export function createDebugFetch(
  source: string,
  originalFetch: typeof fetch = fetch,
) {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const startTime = performance.now()
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url
    const method = init?.method || 'GET'
    let params: any = {
      url,
      method,
    }
    if (init?.body) {
      try {
        params.body = JSON.parse(init.body as string)
      } catch {
        params.body = init.body
      }
    }
    try {
      const response = await originalFetch(input, init)
      const durationMs = Math.round(performance.now() - startTime)

      // Clone response to read body without consuming it for the actual caller
      const clone = response.clone()
      let rawResponse: any
      try {
        rawResponse = await clone.json()
      } catch {
        rawResponse = await clone.text()
      }
      pushDebugLog({
        source,
        tool: `${method} ${new URL(url, window.location.origin).pathname}`,
        params,
        rawResponse,
        processedData: null,
        success: response.ok,
        durationMs,
      })
      return response
    } catch (error: any) {
      const durationMs = Math.round(performance.now() - startTime)
      pushDebugLog({
        source,
        tool: `${method} ${new URL(url, window.location.origin).pathname}`,
        params,
        rawResponse: null,
        processedData: null,
        success: false,
        error: error.message || String(error),
        durationMs,
      })
      throw error
    }
  }
}
